import React, { useState, useEffect } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { subscribeToNewsletter } from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import './Footer.css';

const Footer: React.FC = () => {
  const { showToast } = useToast();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  
  const controls = useAnimation();
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const moveX = clientX - window.innerWidth / 2;
      const moveY = clientY - window.innerHeight / 2;
      const offsetFactor = 15;
      setMousePosition({ 
        x: moveX / offsetFactor, 
        y: moveY / offsetFactor 
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  useEffect(() => {
    controls.start({
      y: [10, 0, 10],
      transition: { 
        repeat: Infinity, 
        duration: 5,
        ease: "easeInOut"
      }
    });
  }, [controls]);
  
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      showToast('Please enter a valid email address', 'error');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit email to newsletter API
      await subscribeToNewsletter(email);
      
      // Show success message as toast notification
      showToast('Thank you! You have been successfully subscribed to our newsletter', 'success');
      
      // Reset form
      setEmail('');
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      showToast(error.message || 'Failed to subscribe. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickLinks = [
    { name: 'Our Services', url: '/services' },
    { name: 'Universities', url: '/universities' },
    // { name: 'Language Classes', url: '/language-classes' },
    { name: 'Gallery', url: '/gallery' },
    { name: 'Contact Us', url: '/contact' },
    { name: 'About Us', url: '/about' },
  ];

  const socialLinks = [
    { icon: 'facebook-f', url: 'https://facebook.com', color: '#1877F2', name: 'Facebook' },
    { icon: 'instagram', url: 'https://instagram.com', color: '#E4405F', name: 'Instagram' },
    { icon: 'twitter', url: 'https://twitter.com', color: '#1DA1F2', name: 'Twitter' },
    { icon: 'youtube', url: 'https://youtube.com', color: '#FF0000', name: 'YouTube' },
  ];

  const accreditations = [
    { name: 'MEXT', logo: '/logos/mext.png' },
    { name: 'JASSO', logo: '/logos/jasso.png' },
    { name: 'JLPT', logo: '/logos/jlpt.png' },
    { name: 'Embassy of Japan', logo: '/logos/embassy.png' },
  ];

  return (
    <footer className="relative bg-slate-900 text-white overflow-hidden">
      {/* Animated wave separator at top */}
      <div className="wave-container">
        <div className="wave wave1"></div>
        <div className="wave wave2"></div>
        <div className="wave wave3"></div>
      </div>
      
      {/* Japanese pattern overlay */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="japanese-pattern-grid"></div>
      </div>
      
      {/* Floating particles */}
      <div className="particle-container">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div 
            key={i}
            className="particle"
            initial={{ 
              x: `${Math.random() * 100}%`, 
              y: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.5 + 0.1,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{ 
              y: ['0%', '100%'],
              x: [`${Math.random() * 100}%`, `${Math.random() * 100}%`]
            }}
            transition={{ 
              repeat: Infinity,
              duration: 20 + Math.random() * 30,
              ease: "linear",
              delay: Math.random() * 20
            }}
          />
        ))}
      </div>
      
      {/* Animated background gradients */}
      <div className="absolute inset-0 opacity-30 pointer-events-none overflow-hidden">
      <motion.div 
          className="absolute top-0 right-0 w-[70vw] h-[70vh] rounded-full bg-gradient-to-b from-primary/40 to-transparent blur-[100px]"
          style={{ 
            x: mousePosition.x * 0.5,
            y: mousePosition.y * 0.5
          }}
          initial={{ opacity: 0.4 }}
        animate={{ 
            opacity: [0.4, 0.2, 0.4],
            scale: [1, 1.1, 1]
        }}
        transition={{ 
          repeat: Infinity,
            duration: 15,
          ease: "easeInOut" 
        }}
      />
      
      <motion.div 
          className="absolute bottom-0 left-0 w-[70vw] h-[70vh] rounded-full bg-gradient-to-t from-secondary/40 to-transparent blur-[100px]"
          style={{ 
            x: mousePosition.x * -0.5,
            y: mousePosition.y * -0.5
          }}
          initial={{ opacity: 0.3 }}
        animate={{ 
            opacity: [0.3, 0.1, 0.3],
            scale: [1, 1.1, 1]
        }}
        transition={{ 
          repeat: Infinity,
          duration: 15,
            delay: 5,
          ease: "easeInOut" 
        }}
      />
      </div>
      
      <div className="container mx-auto px-4 pt-28 pb-12 relative z-10">
        {/* Logo with animation */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="inline-block relative"
          >
            <Link to="/" className="inline-block">
              <img src="/recall.svg" alt="Recall Nepal" className="h-20 object-contain mb-4 mt-4" />
            </Link>
            <motion.div 
              className="absolute -inset-4 -z-10 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full blur-xl"
              animate={{
                opacity: [0.5, 0.8, 0.5],
                scale: [0.95, 1.05, 0.95]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="text-gray-300 max-w-2xl mx-auto"
          >
            Connecting cultures and futures since 2018. We've helped over 2,000+ students transform their Japanese education dreams into successful global careers.
          </motion.p>
        </div>
        
        {/* Main content grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          {/* Column 1: Quick Links */}
          <motion.div 
            className="md:col-span-4 lg:col-span-3"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="footer-heading">
              <motion.span 
                className="footer-heading-icon"
                animate={controls}
              >
                🔗
              </motion.span>
              Quick Links
            </h3>
            <ul className="mt-6 space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                  onHoverStart={() => setHoveredLink(link.name)}
                  onHoverEnd={() => setHoveredLink(null)}
                >
                  <Link to={link.url} className="footer-link-animated">
                    <span className="footer-link-text">{link.name}</span>
                    <motion.span 
                      className="footer-link-arrow"
                      animate={{ 
                        x: hoveredLink === link.name ? 5 : 0,
                        opacity: hoveredLink === link.name ? 1 : 0 
                      }}
                    >
                      →
                    </motion.span>
                    <motion.div 
                      className="footer-link-highlight" 
                      initial={{ scaleX: 0 }}
                      animate={{ 
                        scaleX: hoveredLink === link.name ? 1 : 0 
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>
          
          {/* Column 2: Contact Info */}
            <motion.div
            className="md:col-span-4 lg:col-span-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 className="footer-heading">
              <motion.span 
                className="footer-heading-icon"
                animate={controls}
              >
                📍
              </motion.span>
              Contact Us
            </h3>
            <ul className="mt-6 space-y-5 text-gray-300">
              <motion.li 
                className="flex items-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <div className="contact-icon-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" className="contact-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Our Location</h4>
                  <p>Tokha-9, Gongabu,<br />Kathmandu, Nepal</p>
                </div>
              </motion.li>
              
              <motion.li 
                className="flex items-start"
              initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <div className="contact-icon-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" className="contact-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Email Us</h4>
                  <a href="mailto:info@recallnepal.com" className="hover:text-primary transition-colors">info@recallnepal.com</a>
                </div>
              </motion.li>
              
              <motion.li 
                className="flex items-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
              >
                <div className="contact-icon-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" className="contact-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Call Us</h4>
                  <a href="tel:+977014986209" className="hover:text-primary transition-colors">+977 014986209</a>
                </div>
              </motion.li>
            </ul>
          </motion.div>
          
          {/* Column 3: Newsletter */}
          <motion.div 
            className="md:col-span-4 lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 newsletter-card">
              <h3 className="footer-heading mb-2">
                <motion.span 
                  className="footer-heading-icon"
                  animate={controls}
                >
                  ✉️
                </motion.span>
                Stay Updated
              </h3>
              <p className="text-gray-300 mb-5">
                Subscribe to our newsletter to receive updates on Japanese education, scholarships, and our services.
              </p>
              
              <form onSubmit={handleSubscribe} className="relative">
                <motion.div 
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-3"
                >
                  <div className="newsletter-input-wrapper">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      className="newsletter-input"
                      required
                      disabled={isSubmitting}
                    />
                    <div className="newsletter-input-glow" />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="newsletter-button w-full"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    <span className="relative z-10">
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Subscribing...
                        </>
                      ) : 'Subscribe'}
                    </span>
                    <div className="newsletter-button-glow" />
                  </motion.button>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>
        
        {/* Social Media Section */}
        <motion.div 
          className="py-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="text-2xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-primary via-white to-secondary inline-block">
            Connect With Us
          </h3>
          <div className="flex flex-wrap justify-center gap-5">
                {socialLinks.map((social, index) => (
                  <motion.a
                key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                className="social-3d-wrapper"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + index * 0.1, duration: 0.5 }}
                whileHover={{ y: -10 }}
                style={{
                  '--social-color': social.color
                } as React.CSSProperties}
              >
                <div className="social-3d-front">
                  <i className={`fab fa-${social.icon}`}></i>
                </div>
                <div className="social-3d-back"></div>
                <div className="social-3d-left"></div>
                <div className="social-3d-right"></div>
                <div className="social-3d-top"></div>
                <div className="social-3d-bottom"></div>
                  </motion.a>
                ))}
              </div>
        </motion.div>
        
        {/* Accreditations */}
        {/* <motion.div 
          className="mb-12 py-10 border-t border-b border-white/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <motion.h3 
            className="text-center text-xl font-semibold mb-8 text-white relative inline-block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <span className="inline-block relative">
              Our Accreditations & Partners
              <motion.div 
                className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 1.5 }}
              />
            </span>
          </motion.h3>
          
          <div className="flex flex-wrap justify-center items-center gap-10">
            {accreditations.map((item, index) => (
              <motion.div
                key={item.name}
                className="accreditation-card"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 1.2 + index * 0.1 }}
                whileHover={{ 
                  y: -5, 
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)"
                }}
              >
                <img src={item.logo} alt={item.name} className="h-12 opacity-80" />
                <div className="accreditation-card-glow" />
              </motion.div>
            ))}
          </div>
        </motion.div> */}
        
        {/* Map and Working Hours */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          {/* Map Section */}
          <div className="p-1 rounded-2xl bg-gradient-to-br from-primary/30 via-white/5 to-secondary/30">
            <div className="bg-white/5 backdrop-blur-md rounded-xl overflow-hidden border border-white/10 p-5 h-full">
              <h4 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">🗺️</span> Find Us On Map
              </h4>
              <div className="relative rounded-lg overflow-hidden h-[400px] map-container">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3531.420238020654!2d85.31020947626529!3d27.73518142426174!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb190029fb1633%3A0xe0ae60ee6c3650f2!2sRecall%20Business%20Group%20Pvt.%20Ltd.!5e0!3m2!1sen!2snp!4v1743278032931!5m2!1sen!2snp" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Our location"
                className="absolute inset-0"
              ></iframe>
                <div className="map-glow" />
                </div>
              <div className="mt-3 text-center">
                <a 
                  href="https://www.google.com/maps/place/Recall+Business+Group+Pvt.+Ltd./@27.735177,85.312784,15z/data=!4m6!3m5!1s0x39eb190029fb1633:0xe0ae60ee6c3650f2!8m2!3d27.7351767!4d85.3127844!16s%2Fg%2F11x161p3z1?hl=en&entry=ttu&g_ep=EgoyMDI1MDUxNS4wIKXMDSoASAFQAw%3D%3D" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary hover:text-white transition-colors text-sm font-medium"
                >
                  <span>Get Directions</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          
          {/* Working Hours Section */}
          <div className="p-1 rounded-2xl bg-gradient-to-br from-secondary/30 via-white/5 to-primary/30">
            <div className="bg-white/5 backdrop-blur-md rounded-xl border border-white/10 p-5 h-full">
              <h4 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">⏰</span> Working Hours
              </h4>
              <div className="space-y-4">
                <div className="working-hours-item">
                  <div className="day">Sunday - Thrusday</div>
                  <div className="hours">9:00 AM - 5:00 PM</div>
                </div>
                <div className="working-hours-item">
                  <div className="day">Friday</div>
                  <div className="hours">10:00 AM - 4:00 PM</div>
                </div>
                <div className="working-hours-item">
                  <div className="day">Saturday</div>
                  <div className="hours closed">Closed</div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
                <h5 className="font-medium text-white mb-2 flex items-center">
                  <span className="mr-2">📞</span> Appointment Request
                </h5>
                <p className="text-gray-300 text-sm mb-3">
                  Need a specific time outside our working hours? Contact us to schedule an appointment.
                </p>
                <a 
                  href="mailto:appointments@recallnepal.com" 
                  className="inline-flex items-center justify-center w-full bg-white/10 hover:bg-white/20 transition-colors text-white font-medium py-2 px-4 rounded-lg"
                >
                  Request Appointment
                </a>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 relative">
        <motion.div
            className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-slate-900 px-12 py-1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 }}
          >
            <div className="flex gap-2 justify-center">
              <motion.span
                animate={{ y: [0, -5, 0] }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2,
                  repeatDelay: 1
                }}
              >
                🇯🇵
              </motion.span>
              <motion.span
                animate={{ y: [0, -5, 0] }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2,
                  delay: 0.5,
                  repeatDelay: 1
                }}
              >
                🇳🇵
              </motion.span>
            </div>
          </motion.div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <motion.div 
              className="flex flex-col md:flex-row items-center gap-4 md:gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.5 }}
            >
              <p className="footer-copyright text-center md:text-left">
                © {currentYear} <span className="text-primary font-medium">Recall Nepal</span>. All rights reserved.
              </p>
              <div className="flex gap-6">
                <Link to="/privacy" className="footer-bottom-link hover:text-primary transition-colors">
              Privacy Policy
            </Link>
                <Link to="/terms" className="footer-bottom-link hover:text-primary transition-colors">
              Terms of Service
            </Link>
          </div> 
        </motion.div>
            
            <motion.div 
              className="text-white/60 text-sm flex items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.6 }}
            >
              <span>Made with</span>
              <motion.span
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 1.5,
                  repeatDelay: 1
                }}
                className="inline-block mx-1 text-red-500"
              >
                ❤️
              </motion.span>
              <span>in Nepal</span>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 